import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { ParamsOf, RouteDecl, RouteTable } from '@makan/contracts/client';
import type { SignedIn } from './api.ts';
import { reloadOnFailure } from './boundary.tsx';
import { hostGroup } from './host.ts';

/**
 * Pages as a feature binds them to its routes. The routes are data in the
 * feature's values entry (features/<f>/domain/values.ts); its ui/index.ts
 * binds a lazy page to each (`bindPages`), typed so a route without a page,
 * or a page for no route, does not compile. The shell composes every
 * feature's binding from its generated features.gen.ts (`composeRoutes`),
 * so adding a page never edits the shell, and `/` and sign-in go to the one
 * route marked `staffHome`, never to a path the shell spells.
 *
 * A staff page's path names no group: the shell reads the signed-in staff
 * member's session before it draws the page, and hands it their group.
 */
export type PageProps<D extends RouteDecl> = {
  readonly params: ParamsOf<D['path']>;
  readonly query: URLSearchParams;
} & (D['access'] extends 'staff'
  ? {
      /** The signed-in staff member's group, from their session: never from the URL. */
      readonly groupId: string;
    }
  : unknown);

export type Page<D extends RouteDecl> = ComponentType<PageProps<D>>;

export type PageLoaders<R extends RouteTable> = { readonly [K in keyof R]: () => Promise<{ readonly default: Page<R[K]> }> };

/** The pictures a menu entry can carry; the kit draws them, so a feature names one and imports no icon. */
export type NavIcon = 'home' | 'people' | 'scan' | 'megaphone' | 'ticket' | 'kiosk' | 'kitchen' | 'chart' | 'settings';

/**
 * A staff page in the sidebar: its words, its picture, where it sits
 * (`rank`, against the shell's own entries: home 0, kiosk 50,
 * kitchen 60, reports 70; settings is pinned at the foot), and whom it
 * shows to (`shows`, from the session: a cashier sees no campaigns).
 */
export interface NavDecl {
  readonly label: string;
  readonly icon: NavIcon;
  readonly rank: number;
  readonly shows?: (staff: SignedIn) => boolean;
  /** A short figure beside it (1,204; 3 live), read when the frame first draws for a group; null to show none. */
  readonly count?: (groupId: string) => Promise<string | null>;
}

/** A menu entry may only be a staff route with no params: the frame links to its path as declared. */
export type NavDecls<R extends RouteTable> = { readonly [K in keyof R as R[K]['access'] extends 'staff' ? ([keyof ParamsOf<R[K]['path']>] extends [never] ? K : never) : never]?: NavDecl };

/** What a layer is drawn with: who is signed in. */
export interface LayerProps {
  readonly staff: SignedIn;
}

/**
 * A layer over every page (the feedback toolkit): drawn above whatever the
 * route shows, for a signed-in staff member only. The shell asks `shows`
 * first and imports `load` only when it says yes, so a browser with no staff
 * session, or one the layer does not show to, never downloads its code.
 */
export interface LayerDecl {
  readonly shows: (staff: SignedIn) => Promise<boolean>;
  readonly load: () => Promise<{ readonly default: ComponentType<LayerProps> }>;
}

/** What a feature's ui surface offers the shell: its ui/index.ts exports `ui` of this shape. */
export interface FeatureUi<R extends RouteTable = RouteTable> {
  readonly routes: R;
  readonly pages: PageLoaders<R>;
  readonly nav: NavDecls<R>;
  readonly layer?: LayerDecl;
}

export function bindPages<const R extends RouteTable>(routes: R, pages: PageLoaders<R>, nav: NavDecls<R> = {}, layer?: LayerDecl): FeatureUi<R> {
  return layer === undefined ? { routes, pages, nav } : { routes, pages, nav, layer };
}

/** A staff page in the sidebar, as composed: the route's path and its NavDecl. */
export interface NavEntry extends NavDecl {
  readonly path: string;
}

type AnyPage = ComponentType<{ readonly params: Readonly<Record<string, string>>; readonly query: URLSearchParams; readonly groupId?: string }>;

export interface RouteMatch {
  readonly route: RouteDecl;
  readonly params: Readonly<Record<string, string>>;
  readonly Page: LazyExoticComponent<AnyPage>;
}

export interface ComposedRoutes {
  /** On a group's own host (kit/host.ts), only its public pages match, as `/join` or `/join/<its id>`. */
  readonly match: (path: string) => RouteMatch | undefined;
  /** The hostHome route's path on a group's own host, if a feature marks one. */
  readonly hostHome: string | undefined;
  /** An old staff link, `/g/<group>/<a staff route's path>`: its group and the path the page moved to. Undefined for any other path. */
  readonly oldLink: (path: string) => OldLink | undefined;
  /** The staffHome route's path. */
  readonly staffHome: string;
  /** Every feature's sidebar entries, by rank. */
  readonly nav: readonly NavEntry[];
  /** Every feature's layer, its component lazy. */
  readonly layers: readonly ComposedLayer[];
  /** Fetches every staff page's file ahead, once the first staff page is on screen (it does not compete with that page's own file): a move between them then has nothing to wait for. A file that fails is fetched again by its page. */
  readonly preloadStaffPages: () => void;
}

export interface OldLink {
  readonly groupId: string;
  readonly path: string;
}

export interface ComposedLayer {
  readonly shows: LayerDecl['shows'];
  readonly Layer: LazyExoticComponent<ComponentType<LayerProps>>;
}

/** A page's loader asked once: its page and a preload share the one fetch. A failed fetch is forgotten, so the page asks again (reloadOnFailure). */
function once<T>(load: () => Promise<T>): () => Promise<T> {
  let asked: Promise<T> | undefined;
  return () => {
    asked ??= load().catch((error: unknown) => {
      asked = undefined;
      throw error;
    });
    return asked;
  };
}

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

/**
 * Every feature's pages, matched by path. `:groupId` matches a group's UUID,
 * any other `:param` one path segment. Throws at app start when two routes
 * share a path, a staff route's path names the group, or the routes hold not
 * exactly one `staffHome`.
 */
export function composeRoutes(parts: readonly FeatureUi[]): ComposedRoutes {
  const compiled = parts.flatMap((part) =>
    Object.entries(part.routes).map(([id, route]) => {
      if (route.access === 'staff' && (route.path.includes(':groupId') || route.path.startsWith('/g/'))) throw new Error(`${route.path}: a staff route takes its group from the session, not its path`);
      const names: string[] = [];
      const source = route.path.replace(/:([A-Za-z]\w*)/g, (_match, name: string) => {
        names.push(name);
        return name === 'groupId' ? `(${UUID})` : '([^/]+)';
      });
      const loader = (part.pages as Readonly<Record<string, () => Promise<{ readonly default: AnyPage }>>>)[id];
      if (loader === undefined) throw new Error(`${route.path}: no page bound`);
      // On its group's own host a public page's path drops the group id, which the host names.
      const onHost = route.access === 'public' && names.includes('groupId') ? route.path.replace(/\/:groupId(?=\/|$)/, '').replace(/:([A-Za-z]\w*)/g, '([^/]+)') || '/' : null;
      const load = once(loader);
      return { route, pattern: new RegExp(`^${source}/?$`, 'i'), names, hostPattern: onHost === null ? null : new RegExp(`^${onHost}/?$`, 'i'), load, Page: lazy(reloadOnFailure(load)) };
    }),
  );
  const paths = compiled.map(({ route }) => route.path);
  const twice = paths.find((path, i) => paths.indexOf(path) !== i);
  if (twice !== undefined) throw new Error(`two routes share the path ${twice}`);
  const homes = compiled.filter(({ route }) => route.staffHome === true);
  const [home] = homes;
  if (home === undefined || homes.length > 1) throw new Error(`exactly one route is the staff home; found ${String(homes.length)}`);
  const hostHomes = compiled.filter(({ route }) => route.hostHome === true);
  if (hostHomes.length > 1 || hostHomes.some(({ hostPattern }) => hostPattern === null)) throw new Error('at most one route is the host home, a public route of :groupId');

  const match = (path: string): RouteMatch | undefined => {
    const group = hostGroup();
    for (const { route, pattern, names, hostPattern, Page } of compiled) {
      if (group !== null && hostPattern === null) continue;
      const found = pattern.exec(path);
      if (found) {
        const params: Record<string, string> = Object.fromEntries(names.map((name, i) => [name, decodeURIComponent(found[i + 1] ?? '')]));
        if (group !== null && params['groupId']?.toLowerCase() !== group) continue;
        return { route, Page, params };
      }
      const bare = group === null || hostPattern === null ? null : hostPattern.exec(path);
      if (bare) {
        const rest = names.filter((name) => name !== 'groupId');
        return { route, Page, params: { groupId: group ?? '', ...Object.fromEntries(rest.map((name, i) => [name, decodeURIComponent(bare[i + 1] ?? '')])) } };
      }
    }
    return undefined;
  };
  const oldLinkPattern = new RegExp(`^/g/(${UUID})(/.*)$`, 'i');

  return {
    match,
    hostHome: hostHomes[0]?.route.path.replace(/\/:groupId(?=\/|$)/, '') || undefined,
    oldLink: (path) => {
      const [, groupId, rest] = oldLinkPattern.exec(path) ?? [];
      if (groupId === undefined || rest === undefined || match(rest)?.route.access !== 'staff') return undefined;
      return { groupId: groupId.toLowerCase(), path: rest.replace(/(.)\/$/, '$1') };
    },
    staffHome: home.route.path,
    nav: parts
      .flatMap((part) => Object.entries(part.nav as Readonly<Record<string, NavDecl | undefined>>).flatMap(([id, decl]) => (decl === undefined ? [] : [{ ...decl, path: part.routes[id]?.path ?? '' }])))
      .sort((a, b) => a.rank - b.rank),
    layers: parts.flatMap((part) => (part.layer === undefined ? [] : [{ shows: part.layer.shows, Layer: lazy(reloadOnFailure(part.layer.load)) }])),
    preloadStaffPages: () => {
      for (const { route, load } of compiled) if (route.access === 'staff') void load().catch(() => undefined);
    },
  };
}
